import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { LockClosedIcon } from '@heroicons/react/24/outline'
import { useCallback, useState } from 'react'

import {
    TenantAccess,
    TenantPermission,
    TenantPermissionScope,
    TenantPermissionScopeSchema,
} from '@authy/lib-common/entities'

import { clientAppState } from '@/appState'

import { DebugButton } from './DebugButton'
import { DebugCheckbox } from './DebugCheckbox'

type TenantPermissionsWithoutLimit = Omit<TenantPermission, 'limit'>

const ScopePermissionToggle = observer(
    ({
        access,
        scope,
        permission,
    }: {
        access: TenantAccess
        scope: TenantPermissionScope
        permission: 'read' | 'insert' | 'edit' | 'remove'
    }) => {
        const handleToggle = useCallback(
            (checked: boolean) => {
                runInAction(() => {
                    if (!access.permissions[scope]) {
                        access.permissions[scope] = {
                            read: false,
                            insert: false,
                            edit: false,
                            remove: false,
                        }
                    }

                    const perms = access.permissions[scope] as TenantPermissionsWithoutLimit
                    perms[permission] = checked
                })
            },
            [access.permissions, permission, scope]
        )

        return (
            <DebugCheckbox
                label={permission}
                secondary
                checked={access.permissions?.[scope]?.[permission] || false}
                onToggle={handleToggle}
            />
        )
    }
)

export const ChangeTenantAccess = observer(() => {
    const { tenantAccess } = clientAppState

    const [open, setOpen] = useState(false)

    const handleToggleOpen = useCallback(() => {
        setOpen((open) => !open)
    }, [])

    if (!tenantAccess) {
        return null
    }

    return (
        <>
            {open && (
                <div className="absolute bottom-full right-0 h-96 w-60 overflow-auto rounded-lg border border-neutral-300 bg-white p-2 shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
                    <DebugCheckbox
                        label="User is owner"
                        checked={tenantAccess.owner}
                        onToggle={(checked) => {
                            runInAction(() => {
                                tenantAccess.owner = checked

                                // putting owner will clear all permissions
                                tenantAccess.permissions = {}
                            })
                        }}
                    />
                    {!tenantAccess.owner && (
                        <>
                            <div className="my-2 border-t border-t-neutral-200" />
                            <div className="flex flex-col gap-y-2">
                                {Object.keys(TenantPermissionScopeSchema.enum).map((scope) => (
                                    <div key={scope} className="shrink-0">
                                        <div className="text-xs font-medium">{scope}</div>
                                        <div className="flex items-center justify-between">
                                            <ScopePermissionToggle
                                                access={tenantAccess}
                                                scope={scope as TenantPermissionScope}
                                                permission="read"
                                            />
                                            <ScopePermissionToggle
                                                access={tenantAccess}
                                                scope={scope as TenantPermissionScope}
                                                permission="edit"
                                            />
                                            <ScopePermissionToggle
                                                access={tenantAccess}
                                                scope={scope as TenantPermissionScope}
                                                permission="insert"
                                            />
                                            <ScopePermissionToggle
                                                access={tenantAccess}
                                                scope={scope as TenantPermissionScope}
                                                permission="remove"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
            <DebugButton Icon={LockClosedIcon} onClick={handleToggleOpen} />
        </>
    )
})
